// Centered modal overlay — replaces the inline-appended "form at bottom-left"
// pattern. Use openModal({ id, title, html, onClose }) for any future form too.
const OPEN_CLASS = 'modal-open';

export interface ModalOptions {
  id: string;
  title?: string;
  subtitle?: string;
  html: string;            // innerHTML for the modal panel (typically a <form>)
  onSubmit?: (form: HTMLFormElement, close: () => void) => Promise<void> | void;
  onMount?: (host: HTMLElement, close: () => void) => void;
  width?: number;          // px
}

export function openModal(opts: ModalOptions): () => void {
  // Close any modal of the same id or any existing modal first so we never stack.
  closeModal(opts.id);
  document.querySelectorAll('.app-modal').forEach((el) => el.remove());

  const host = document.createElement('div');
  host.className = 'app-modal';
  host.id = opts.id + '-host';
  host.setAttribute('role', 'dialog');
  host.setAttribute('aria-modal', 'true');
  const w = opts.width || 540;
  host.style.cssText = `position:fixed;inset:0;background:rgba(10,18,38,0.5);backdrop-filter:blur(4px);z-index:96;display:grid;place-items:center;padding:24px;`;

  const panel = document.createElement('div');
  panel.style.cssText = `position:relative;max-width:${w}px;width:100%;max-height:90vh;overflow:auto;`;

  const header = (opts.title || opts.subtitle)
    ? `<div class="row" style="margin-bottom:16px"><div style="flex:1;min-width:0">
        ${opts.title ? '<div class="t-h2">' + escapeHtml(opts.title) + '</div>' : ''}
        ${opts.subtitle ? '<div class="t-meta">' + escapeHtml(opts.subtitle) + '</div>' : ''}
       </div>
       <button type="button" class="btn btn-ghost btn-sm" data-modal-close aria-label="Close">✕</button></div>`
    : `<button type="button" class="btn btn-ghost btn-sm" data-modal-close aria-label="Close" style="position:absolute;top:12px;right:12px">✕</button>`;

  panel.innerHTML = '<div class="card card-pad-lg" style="position:relative">' + header + opts.html + '</div>';
  host.appendChild(panel);
  document.body.appendChild(host);
  document.body.classList.add(OPEN_CLASS);

  const close = () => closeModal(opts.id);
  host.addEventListener('click', (e) => { if (e.target === host) close(); });
  panel.querySelectorAll('[data-modal-close]').forEach((b) => b.addEventListener('click', close));
  document.addEventListener('keydown', escHandler);

  const form = panel.querySelector<HTMLFormElement>('form');
  if (form && opts.onSubmit) {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      await opts.onSubmit!(form, close);
    });
  }
  if (opts.onMount) opts.onMount(panel, close);

  return close;
}

function escHandler(e: KeyboardEvent) {
  if (e.key === 'Escape') {
    const top = document.querySelector('.app-modal:last-of-type') as HTMLElement | null;
    if (top && top.id.endsWith('-host')) closeModal(top.id.replace(/-host$/, ''));
  }
}

export function closeModal(id?: string): void {
  if (id) {
    const el = document.getElementById(id + '-host');
    if (el) el.remove();
  } else {
    document.querySelectorAll('.app-modal').forEach((el) => el.remove());
  }
  if (!document.querySelector('.app-modal')) document.body.classList.remove(OPEN_CLASS);
}

function escapeHtml(s: string): string {
  return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
